/**
 * Content block renderers — one component per block type, dispatched by
 * `BlockView`. `text`, `embed`, `card`, and `consent` HTML is config-authored
 * and treated as trusted (PLAN.md §11) — never end-user input.
 */
import { useContext, useState } from "preact/hooks";
import type { AccordionBlock, Block, StepsBlock, VideoBlock } from "../types";
import { selectVariant, substituteTokens } from "../logic/dynamic";
import { iconSvg } from "./icons";
import { ProgressContext } from "./progress-context";
import { DynamicContext } from "./dynamic-context";

/** Progress bar — reads funnel progress from context so it can be placed
 *  within a step's block flow (e.g. directly above the question). */
function ProgressBar() {
  const pct = useContext(ProgressContext);
  return (
    <div class="fe-progress" role="progressbar" aria-valuenow={pct}>
      <div class="fe-progress-fill" style={`width:${Math.max(pct, 5)}%`} />
    </div>
  );
}

/** Render an icon token as inline SVG, or fall back to literal text (emoji). */
function Icon({ token }: { token: string }) {
  const svg = iconSvg(token);
  if (svg) {
    return <span class="fe-icon" dangerouslySetInnerHTML={{ __html: svg }} />;
  }
  return <span class="fe-choice-icon">{token}</span>;
}

function VideoView({ block }: { block: VideoBlock }) {
  if (block.provider === "mp4") {
    return (
      <video
        class="fe-img"
        src={block.src}
        controls
        autoPlay={block.autoplay}
        muted={block.autoplay}
        playsInline
      />
    );
  }
  const src =
    block.provider === "youtube"
      ? `https://www.youtube.com/embed/${block.src}${block.autoplay ? "?autoplay=1&mute=1" : ""}`
      : `https://player.vimeo.com/video/${block.src}${block.autoplay ? "?autoplay=1&muted=1" : ""}`;
  return (
    <div style="position:relative;padding-top:56.25%">
      <iframe
        title="video"
        src={src}
        allow="autoplay; fullscreen; picture-in-picture"
        style="position:absolute;inset:0;width:100%;height:100%;border:0;border-radius:var(--fe-radius)"
      />
    </div>
  );
}

function StepsView({ block }: { block: StepsBlock }) {
  const current = block.current ?? 0;
  return (
    <div class="fe-steps">
      {block.labels.map((label, i) => {
        const state = i < current ? "done" : i === current ? "active" : "todo";
        return (
          <>
            {i > 0 ? <span class={`fe-steps-line fe-steps-line--${i <= current ? "done" : "todo"}`} /> : null}
            <div class={`fe-step fe-step--${state}`}>
              <span class="fe-step-dot">{i < current ? "✓" : i + 1}</span>
              <span class="fe-step-label">{label}</span>
            </div>
          </>
        );
      })}
    </div>
  );
}

function AccordionView({ block }: { block: AccordionBlock }) {
  const dyn = useContext(DynamicContext);
  const [open, setOpen] = useState<number | null>(
    block.open_index ?? null,
  );
  return (
    <div class="fe-accordion">
      {block.items.map((item, i) => {
        const isOpen = open === i;
        return (
          <div class={`fe-acc-item${isOpen ? " fe-acc-item--open" : ""}`}>
            <button
              type="button"
              class="fe-acc-q"
              aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? null : i)}
            >
              <span>{substituteTokens(item.q, dyn, false)}</span>
              <span class="fe-acc-mark" aria-hidden="true">{isOpen ? "–" : "+"}</span>
            </button>
            {isOpen ? (
              <div
                class="fe-acc-a fe-text"
                dangerouslySetInnerHTML={{ __html: substituteTokens(item.a, dyn, true) }}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function BlockView({ block }: { block: Block }) {
  const dyn = useContext(DynamicContext);
  /** {{token}} substitution — tx: JSX child (no escape), th: innerHTML (escape). */
  const tx = (s: string): string => substituteTokens(s, dyn, false);
  const th = (s: string): string => substituteTokens(s, dyn, true);

  switch (block.type) {
    case "heading": {
      const cls = `fe-h fe-h${block.level ?? 1}`;
      const style = block.align ? `text-align:${block.align}` : undefined;
      const text = tx(block.text);
      if (block.level === 2) return <h2 class={cls} style={style}>{text}</h2>;
      if (block.level === 3) return <h3 class={cls} style={style}>{text}</h3>;
      return <h1 class={cls} style={style}>{text}</h1>;
    }

    case "text":
      return (
        <div
          class="fe-text"
          style={block.align ? `text-align:${block.align}` : undefined}
          dangerouslySetInnerHTML={{ __html: th(block.html) }}
        />
      );

    case "image":
      return (
        <img
          class="fe-img"
          src={tx(block.src)}
          alt={tx(block.alt)}
          style={`${block.width_pct ? `width:${block.width_pct}%;` : ""}${
            block.align === "center"
              ? "margin:0 auto;"
              : block.align === "right"
                ? "margin-left:auto;"
                : ""
          }`}
        />
      );

    case "video":
      return <VideoView block={block} />;

    case "embed":
      return <div dangerouslySetInnerHTML={{ __html: th(block.html) }} />;

    case "card": {
      const variant = block.variant ?? "info";
      return (
        <div class={`fe-card fe-card--${variant}`}>
          {block.title ? (
            <h3 class="fe-h fe-h3" style="margin-bottom:6px">
              {block.icon ? <Icon token={block.icon} /> : null}{" "}
              {tx(block.title)}
            </h3>
          ) : null}
          <div class="fe-text" dangerouslySetInnerHTML={{ __html: th(block.html) }} />
        </div>
      );
    }

    case "divider":
      return <hr class="fe-divider" />;

    case "spacer": {
      const h = block.size === "lg" ? 32 : block.size === "sm" ? 8 : 18;
      return <div style={`height:${h}px`} />;
    }

    case "list": {
      const style = block.style ?? "check";
      if (style === "check") {
        return (
          <ul class="fe-list">
            {block.items.map((item, i) => (
              <li
                class="fe-list-check fe-fade"
                style={block.animate ? `animation-delay:${i * 0.12}s` : undefined}
              >
                <span>{tx(item)}</span>
              </li>
            ))}
          </ul>
        );
      }
      return (
        <ul class={`fe-list fe-list--${style}`}>
          {block.items.map((item) => (
            <li>{tx(item)}</li>
          ))}
        </ul>
      );
    }

    case "progress":
      return <ProgressBar />;

    case "steps":
      return <StepsView block={block} />;

    case "accordion":
      return <AccordionView block={block} />;

    case "dynamic":
      return <Blocks blocks={selectVariant(block, dyn)} />;

    case "dq_reasons":
      // Placeholder block — rendered inline by ResultPage on disqualified
      // outcomes. Outside that context (e.g., if someone puts it on a
      // qualified result tier) it renders nothing.
      return null;

    default: {
      const _never: never = block;
      return <span>{`unknown block: ${JSON.stringify(_never)}`}</span>;
    }
  }
}

export function Blocks({ blocks }: { blocks: Block[] }) {
  return (
    <>
      {blocks.map((b, i) => (
        <BlockView key={i} block={b} />
      ))}
    </>
  );
}
